import { useEffect, useMemo, useState } from "react";
import {
  X, ArrowRight, ArrowLeft, Check, Info, Sparkles, ShieldCheck,
  Loader2, Home as HomeIcon, Flame, Snowflake, Layers, Package, PhoneCall,
} from "lucide-react";
import { calculateIASimulator } from "@/lib/api";

/**
 * IASimulatorWizard — Multi-project national simulator (Phase 1 & 2)
 * Follows Bleu Marianne (#0055A4) institutional aesthetic.
 * Client email is INTENTIONALLY NOT sent. Only Direction is notified.
 * A specialist will call the user back to confirm the aids amounts.
 */

const PROJECTS = [
  { key: "pac_air_eau", label: "Pompe à chaleur Air/Eau", tag: "Chauffage", color: "#0055A4", icon: Snowflake, hint: "Solution la plus subventionnée" },
  { key: "pac_eau_eau", label: "PAC Eau/Eau (géothermie)", tag: "Chauffage", color: "#0055A4", icon: Snowflake, hint: "Rendement maximal" },
  { key: "chauffe_eau_thermo", label: "Chauffe-eau thermodynamique", tag: "ECS", color: "#0369A1", icon: Flame, hint: "Ballon d'eau chaude" },
  { key: "poele_granules", label: "Poêle à granulés", tag: "Chauffage bois", color: "#EA580C", icon: Flame, hint: "Bois local, biosourcé" },
  { key: "chaudiere_biomasse", label: "Chaudière biomasse", tag: "Chauffage bois", color: "#EA580C", icon: Flame, hint: "Remplacement fioul" },
  { key: "isolation_combles_perdus", label: "Isolation combles perdus", tag: "Isolation", color: "#10B981", icon: Layers, hint: "1er réflexe pour économiser" },
  { key: "isolation_rampants", label: "Isolation rampants toiture", tag: "Isolation", color: "#10B981", icon: Layers, hint: "Toiture aménagée" },
  { key: "isolation_murs_ext", label: "Isolation murs extérieurs (ITE)", tag: "Isolation", color: "#10B981", icon: Layers, hint: "Efficacité maximale" },
  { key: "isolation_murs_int", label: "Isolation murs intérieurs (ITI)", tag: "Isolation", color: "#10B981", icon: Layers, hint: "Solution économique" },
  { key: "fenetres", label: "Fenêtres double/triple vitrage", tag: "Menuiseries", color: "#0055A4", icon: Package, hint: "Remplacement complet" },
];

const ENERGIES = [
  { v: "gaz", l: "Gaz" },
  { v: "fioul", l: "Fioul" },
  { v: "electricite", l: "Électricité" },
  { v: "bois", l: "Bois" },
  { v: "autre", l: "Autre" },
];

const initial = {
  project_type: "",
  postal_code: "", city: "", address: "",
  housing_type: "maison",
  ownership_status: "proprietaire_occupant",
  residence: "principale",
  construction_year: "",
  surface_m2: "",
  travaux_surface_m2: "",
  quantity: "",
  current_heating: "",
  current_energy: "",
  household_size: "",
  fiscal_income: "",
  first_name: "", last_name: "", phone: "", email: "",
  gdpr_consent: false,
};

export default function IASimulatorWizard({ open, onClose }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [open]);
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => { setStep(0); setForm(initial); setResult(null); setError(""); }, 250);
      return () => clearTimeout(t);
    }
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const isIsolation = (form.project_type || "").startsWith("isolation");
  const isWindows = form.project_type === "fenetres";
  const isHeating = !!form.project_type && !isIsolation && !isWindows;

  const steps = useMemo(() => {
    const s = [{ id: "intro" }, { id: "project" }];
    if (form.project_type) {
      s.push({ id: "location" });
      s.push({ id: "logement" });
      if (isIsolation) s.push({ id: "isolation-surface" });
      else if (isWindows) s.push({ id: "windows-qty" });
      else s.push({ id: "heating-current" });
      s.push({ id: "household" });
      s.push({ id: "contact" });
      s.push({ id: "consent" });
    }
    return s;
  }, [form.project_type, isIsolation, isWindows]);

  const total = steps.length;
  const current = steps[step] || {};
  const progress = Math.round(((step + 1) / total) * 100);

  const canNext = () => {
    switch (current.id) {
      case "intro": return true;
      case "project": return !!form.project_type;
      case "location": return form.postal_code.length === 5 && form.city.length > 0;
      case "logement": return !!form.housing_type && !!form.ownership_status;
      case "isolation-surface": return Number(form.travaux_surface_m2) > 0;
      case "windows-qty": return Number(form.quantity) > 0;
      case "heating-current": return Number(form.surface_m2) > 0 && !!form.current_energy;
      case "household": return Number(form.household_size) > 0 && Number(form.fiscal_income) > 0;
      case "contact": return form.first_name && form.last_name && /\S+@\S+\.\S+/.test(form.email) && form.phone.length >= 6;
      case "consent": return form.gdpr_consent === true;
      default: return true;
    }
  };

  const next = () => {
    if (!canNext()) { setError("Merci de compléter les champs requis."); return; }
    setError("");
    if (current.id === "consent") return submit();
    setStep((s) => Math.min(total - 1, s + 1));
  };
  const prev = () => { setError(""); setStep((s) => Math.max(0, s - 1)); };

  const submit = async () => {
    setSubmitting(true); setError("");
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        address: form.address.trim(),
        postal_code: form.postal_code.trim(),
        city: form.city.trim(),
        household_size: Number(form.household_size) || 1,
        fiscal_income: Number(form.fiscal_income) || 0,
        ownership_status: form.ownership_status,
        residence: form.residence,
        project_type: form.project_type,
        surface_m2: Number(form.surface_m2) || 0,
        travaux_surface_m2: Number(form.travaux_surface_m2) || 0,
        quantity: Number(form.quantity) || 0,
        construction_year: form.construction_year ? Number(form.construction_year) : null,
        current_heating: form.current_heating,
        current_energy: form.current_energy,
        housing_type: form.housing_type,
        gdpr_consent: form.gdpr_consent,
      };
      const data = await calculateIASimulator(payload);
      setResult(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Une erreur est survenue. Merci de réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="ia-simulator-modal"
      className="fixed inset-0 z-[100] bg-slate-900/70 backdrop-blur-sm flex items-stretch justify-center overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="relative w-full max-w-3xl my-8 mx-4 sm:mx-8 bg-white rounded-3xl shadow-2xl border border-slate-200 flex flex-col">
        {/* HEADER */}
        <div className="flex items-center justify-between px-6 sm:px-8 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-[#0055A4] to-[#003D7A] grid place-items-center">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="text-[10px] tracking-[.22em] uppercase text-[#0055A4] font-semibold">ECONATION IA</div>
              <div className="text-slate-900 font-semibold text-sm">Simulateur National · Barèmes 2026</div>
            </div>
          </div>
          <button
            data-testid="ia-simulator-close"
            onClick={onClose}
            className="h-9 w-9 rounded-full grid place-items-center bg-slate-100 hover:bg-slate-200 text-slate-600 transition"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* PROGRESS */}
        {!result && (
          <div className="px-6 sm:px-8 pt-4">
            <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1.5">
              <span>Étape {step + 1} sur {total}</span>
              <span>{progress}%</span>
            </div>
            <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#0055A4] to-[#10B981] transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        {/* BODY */}
        <div className="flex-1 px-6 sm:px-10 py-8 sm:py-10 overflow-y-auto">
          {result ? (
            <ResultScreen result={result} onClose={onClose} />
          ) : (
            <StepBody current={current} form={form} set={set} />
          )}
          {error && !result && (
            <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="ia-simulator-error">
              {error}
            </div>
          )}
        </div>

        {/* FOOTER */}
        {!result && (
          <div className="px-6 sm:px-8 py-4 border-t border-slate-100 flex items-center justify-between gap-3 bg-slate-50/70 rounded-b-3xl">
            <button
              onClick={prev}
              disabled={step === 0}
              data-testid="ia-simulator-prev"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm text-slate-600 disabled:opacity-40 hover:bg-white hover:text-slate-900 transition"
            >
              <ArrowLeft className="h-4 w-4" /> Retour
            </button>
            <button
              onClick={next}
              disabled={submitting || !canNext()}
              data-testid="ia-simulator-next"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#0055A4] to-[#003D7A] hover:from-[#003D7A] hover:to-[#0055A4] disabled:opacity-40 disabled:cursor-not-allowed px-6 py-2.5 text-white text-sm font-semibold tracking-wide shadow-lg shadow-[#0055A4]/25 transition"
            >
              {submitting ? (<><Loader2 className="h-4 w-4 animate-spin" /> Calcul...</>) : (
                current.id === "consent" ? (<>Calculer mes aides <Sparkles className="h-4 w-4" /></>) : (<>Continuer <ArrowRight className="h-4 w-4" /></>)
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
 * STEP BODY
 * ============================================================ */
function StepBody({ current, form, set }) {
  switch (current.id) {
    case "intro": return <IntroStep />;
    case "project": return <ProjectStep form={form} set={set} />;
    case "location": return <LocationStep form={form} set={set} />;
    case "logement": return <LogementStep form={form} set={set} />;
    case "isolation-surface": return <IsolationSurfaceStep form={form} set={set} />;
    case "windows-qty": return <WindowsStep form={form} set={set} />;
    case "heating-current": return <HeatingStep form={form} set={set} />;
    case "household": return <HouseholdStep form={form} set={set} />;
    case "contact": return <ContactStep form={form} set={set} />;
    case "consent": return <ConsentStep form={form} set={set} />;
    default: return null;
  }
}

/* ============================================================
 * INDIVIDUAL STEPS
 * ============================================================ */
function IntroStep() {
  return (
    <div className="text-center max-w-xl mx-auto" data-testid="ia-step-intro">
      <div className="inline-flex items-center gap-2 rounded-full bg-[#EFF6FF] text-[#0055A4] text-[11px] tracking-[.18em] uppercase font-semibold px-3 py-1.5 mb-5">
        <Sparkles className="h-3.5 w-3.5" /> Nouveauté · IA barèmes 2026
      </div>
      <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-3">
        Estimez vos aides<br className="hidden sm:block" /> MaPrimeRénov' & CEE
      </h2>
      <p className="text-slate-600 leading-relaxed">
        Notre IA analyse en temps réel votre <strong>MaPrimeRénov'</strong> et vos <strong>primes CEE</strong>
        selon votre projet, votre foyer et votre zone climatique.
        Un spécialiste ECONATION vous rappellera pour confirmer le montant total.
      </p>
      <div className="grid grid-cols-3 gap-3 mt-8">
        {[
          { icon: Snowflake, l: "Chauffage" },
          { icon: Layers, l: "Isolation" },
          { icon: Package, l: "Menuiseries" },
        ].map((x, i) => {
          const Ic = x.icon;
          return (
            <div key={i} className="rounded-2xl border border-slate-200 bg-slate-50/50 p-4 flex flex-col items-center gap-2">
              <div className="h-10 w-10 rounded-xl bg-[#EFF6FF] text-[#0055A4] grid place-items-center">
                <Ic className="h-5 w-5" />
              </div>
              <div className="text-slate-800 font-medium text-sm">{x.l}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProjectStep({ form, set }) {
  return (
    <div data-testid="ia-step-project">
      <StepTitle title="Quel projet souhaitez-vous réaliser ?" hint="Sélectionnez un chantier à la fois." />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {PROJECTS.map((p) => {
          const Ic = p.icon;
          const active = form.project_type === p.key;
          return (
            <button
              key={p.key}
              onClick={() => set("project_type", p.key)}
              data-testid={`ia-project-${p.key}`}
              className={`group text-left rounded-2xl border-2 p-4 transition-all ${
                active ? "border-[#0055A4] bg-[#EFF6FF] shadow-md" : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm"
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`h-10 w-10 rounded-lg grid place-items-center flex-shrink-0 transition ${active ? "bg-[#0055A4] text-white" : "bg-slate-100 text-slate-700"}`}>
                  <Ic className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{p.tag}</div>
                  <div className="text-slate-900 font-semibold text-sm mt-0.5 truncate">{p.label}</div>
                  <div className="text-slate-500 text-xs mt-0.5 truncate">{p.hint}</div>
                </div>
                {active && <Check className="h-4 w-4 text-[#0055A4] flex-shrink-0 ml-auto" strokeWidth={3} />}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function LocationStep({ form, set }) {
  return (
    <div data-testid="ia-step-location">
      <StepTitle title="Où se situe votre logement ?" hint="Le code postal détermine votre zone climatique et vos plafonds MaPrimeRénov'." />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Code postal">
          <input
            data-testid="ia-postal"
            value={form.postal_code}
            onChange={(e) => set("postal_code", e.target.value.replace(/\D/g, "").slice(0, 5))}
            placeholder="75012"
            inputMode="numeric"
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0055A4]/30"
          />
        </Field>
        <Field label="Ville">
          <input
            data-testid="ia-city"
            value={form.city}
            onChange={(e) => set("city", e.target.value)}
            placeholder="Paris"
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0055A4]/30"
          />
        </Field>
        <Field label="Adresse complète (facultatif)" full>
          <input
            data-testid="ia-address"
            value={form.address}
            onChange={(e) => set("address", e.target.value)}
            placeholder="12 rue de la République"
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0055A4]/30"
          />
        </Field>
      </div>
    </div>
  );
}

function LogementStep({ form, set }) {
  return (
    <div data-testid="ia-step-logement">
      <StepTitle title="Votre logement" hint="Le statut d'occupation impacte l'éligibilité MaPrimeRénov'." />
      <div className="space-y-5">
        <Radios
          label="Type de logement"
          testidPrefix="ia-housing"
          value={form.housing_type}
          onChange={(v) => set("housing_type", v)}
          options={[{ v: "maison", l: "Maison individuelle" }, { v: "appartement", l: "Appartement" }]}
        />
        <Radios
          label="Statut d'occupation"
          testidPrefix="ia-ownership"
          value={form.ownership_status}
          onChange={(v) => set("ownership_status", v)}
          options={[
            { v: "proprietaire_occupant", l: "Propriétaire occupant" },
            { v: "proprietaire_bailleur", l: "Propriétaire bailleur" },
            { v: "locataire", l: "Locataire" },
          ]}
        />
        <Radios
          label="Type de résidence"
          testidPrefix="ia-residence"
          value={form.residence}
          onChange={(v) => set("residence", v)}
          options={[{ v: "principale", l: "Résidence principale" }, { v: "secondaire", l: "Résidence secondaire" }]}
        />
        <Field label="Année de construction (facultatif)" full>
          <input
            data-testid="ia-construction-year"
            value={form.construction_year}
            onChange={(e) => set("construction_year", e.target.value.replace(/\D/g, "").slice(0, 4))}
            placeholder="1985"
            inputMode="numeric"
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0055A4]/30"
          />
        </Field>
      </div>
    </div>
  );
}

function IsolationSurfaceStep({ form, set }) {
  return (
    <div data-testid="ia-step-isolation">
      <StepTitle title="Surface à isoler" hint="Indiquez la surface totale en mètres carrés à isoler." />
      <Field label="Surface travaux (m²)" full>
        <input
          data-testid="ia-travaux-surface"
          value={form.travaux_surface_m2}
          onChange={(e) => set("travaux_surface_m2", e.target.value.replace(/\D/g, "").slice(0, 4))}
          placeholder="80"
          inputMode="numeric"
          className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0055A4]/30 text-lg font-semibold"
        />
      </Field>
      <InfoBanner text="Les combles perdus font en moyenne 60 à 100 m². L'ITE couvre l'ensemble des façades." />
    </div>
  );
}

function WindowsStep({ form, set }) {
  return (
    <div data-testid="ia-step-windows">
      <StepTitle title="Combien de fenêtres à remplacer ?" hint="Incluez portes-fenêtres et baies vitrées." />
      <Field label="Nombre d'ouvertures" full>
        <input
          data-testid="ia-quantity"
          value={form.quantity}
          onChange={(e) => set("quantity", e.target.value.replace(/\D/g, "").slice(0, 3))}
          placeholder="6"
          inputMode="numeric"
          className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0055A4]/30 text-lg font-semibold"
        />
      </Field>
      <InfoBanner text="Un pavillon standard compte généralement entre 5 et 12 ouvertures." />
    </div>
  );
}

function HeatingStep({ form, set }) {
  return (
    <div data-testid="ia-step-heating">
      <StepTitle title="Votre logement & chauffage actuel" hint="Ces informations nous permettent de dimensionner l'équipement." />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Surface habitable (m²)">
          <input
            data-testid="ia-surface"
            value={form.surface_m2}
            onChange={(e) => set("surface_m2", e.target.value.replace(/\D/g, "").slice(0, 4))}
            placeholder="110"
            inputMode="numeric"
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0055A4]/30"
          />
        </Field>
        <Field label="Chauffage actuel (libellé libre)">
          <input
            data-testid="ia-current-heating"
            value={form.current_heating}
            onChange={(e) => set("current_heating", e.target.value)}
            placeholder="Chaudière gaz"
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0055A4]/30"
          />
        </Field>
        <div className="sm:col-span-2">
          <Radios
            label="Énergie principale utilisée"
            testidPrefix="ia-energy"
            value={form.current_energy}
            onChange={(v) => set("current_energy", v)}
            options={ENERGIES.map((e) => ({ v: e.v, l: e.l }))}
          />
        </div>
      </div>
    </div>
  );
}

function HouseholdStep({ form, set }) {
  return (
    <div data-testid="ia-step-household">
      <StepTitle title="Composition et revenus du foyer" hint="Barèmes ANAH 2026 — IDF ou hors IDF selon votre code postal." />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Nombre de personnes au foyer">
          <input
            data-testid="ia-household-size"
            value={form.household_size}
            onChange={(e) => set("household_size", e.target.value.replace(/\D/g, "").slice(0, 2))}
            placeholder="4"
            inputMode="numeric"
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0055A4]/30"
          />
        </Field>
        <Field label="Revenu fiscal de référence (€/an)">
          <input
            data-testid="ia-fiscal-income"
            value={form.fiscal_income}
            onChange={(e) => set("fiscal_income", e.target.value.replace(/\D/g, "").slice(0, 8))}
            placeholder="28000"
            inputMode="numeric"
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0055A4]/30"
          />
        </Field>
      </div>
      <InfoBanner text="Le revenu fiscal figure sur votre dernier avis d'imposition. Aucun document n'est demandé à cette étape." />
    </div>
  );
}

function ContactStep({ form, set }) {
  return (
    <div data-testid="ia-step-contact">
      <StepTitle title="Vos coordonnées" hint="Un spécialiste ECONATION vous rappellera dans les 24h ouvrées." />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Prénom">
          <input data-testid="ia-first-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)}
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0055A4]/30"
            placeholder="Jean" />
        </Field>
        <Field label="Nom">
          <input data-testid="ia-last-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)}
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0055A4]/30"
            placeholder="Dupont" />
        </Field>
        <Field label="Téléphone">
          <input data-testid="ia-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} inputMode="tel"
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0055A4]/30"
            placeholder="06 12 34 56 78" />
        </Field>
        <Field label="Email">
          <input data-testid="ia-email" value={form.email} onChange={(e) => set("email", e.target.value)} type="email"
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0055A4]/30"
            placeholder="jean.dupont@email.fr" />
        </Field>
      </div>
    </div>
  );
}

function ConsentStep({ form, set }) {
  return (
    <div data-testid="ia-step-consent">
      <StepTitle title="Dernière étape — Consentement" hint="Vos données sont traitées conformément au RGPD." />
      <label className="flex items-start gap-3 rounded-2xl border-2 border-slate-200 bg-white p-5 cursor-pointer hover:border-[#0055A4]/40 transition">
        <input
          type="checkbox"
          data-testid="ia-consent"
          checked={form.gdpr_consent}
          onChange={(e) => set("gdpr_consent", e.target.checked)}
          className="mt-1 h-5 w-5 accent-[#0055A4]"
        />
        <div className="text-sm text-slate-700 leading-relaxed">
          J'accepte que <strong>ECONATION</strong> me contacte pour finaliser mon estimation et me proposer un accompagnement dans mes démarches
          MaPrimeRénov' & CEE. Mes données ne seront pas cédées à des tiers et je peux exercer mes droits RGPD à tout moment via
          <a href="/confidentialite" className="text-[#0055A4] underline underline-offset-2 mx-1">notre politique de confidentialité</a>.
        </div>
      </label>
      <div className="mt-5 flex items-center gap-2 text-slate-500 text-xs">
        <ShieldCheck className="h-4 w-4 text-[#10B981]" />
        Aucun email n'est envoyé sans votre validation. Un conseiller vous rappellera d'abord.
      </div>
    </div>
  );
}

/* ============================================================
 * RESULT SCREEN
 * ============================================================ */
function ResultScreen({ result, onClose }) {
  const fmt = (v) => `${Number(v || 0).toLocaleString("fr-FR")} €`;
  const emojiByCat = { grand_precaire: "🔵", precaire: "🟡", classique: "🌸" };
  const emoji = emojiByCat[result.category] || "•";

  return (
    <div className="max-w-2xl mx-auto" data-testid="ia-simulator-result">
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 rounded-full bg-[#EFF6FF] text-[#0055A4] text-[11px] tracking-[.2em] uppercase font-semibold px-3 py-1.5 mb-4">
          <Sparkles className="h-3.5 w-3.5" /> Estimation générée
        </div>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-2" data-testid="ia-result-total">
          Jusqu'à <span className="text-[#0055A4]">{fmt(result.total_aides)}</span> d'aides cumulées
        </h2>
        <p className="text-slate-600 text-sm">
          {result.project_label} · Profil <strong>{emoji} {result.category_label}</strong> · Zone {result.zone_climatique}
        </p>
        <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-slate-900 text-white text-[11px] tracking-wider uppercase font-semibold px-3 py-1.5">
          📁 Dossier n° {result.dossier_number}
        </div>
      </div>

      {/* Detail table */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <Row label="Coût travaux estimé" value={fmt(result.cout_travaux_estime)} muted />
        <Row label="MaPrimeRénov'" value={`+ ${fmt(result.prime_mpr)}`} highlight />
        <Row label="Prime CEE (Certificats d'Économies d'Énergie)" value={`+ ${fmt(result.prime_cee)}`} highlight />
        <Row label="Total aides cumulées" value={fmt(result.total_aides)} strong />
        <Row label="Reste à charge estimé" value={fmt(result.reste_a_charge)} strong muted />
        <Row label="Économies annuelles" value={`${fmt(result.economies_annuelles_estimees)}/an`} />
        <Row label="Retour sur investissement" value={`${result.roi_annees} an(s)`} />
      </div>

      {/* Callback disclaimer */}
      <div className="mt-6 rounded-2xl bg-gradient-to-br from-[#EFF6FF] to-[#DBEAFE] border border-[#0055A4]/20 p-5 sm:p-6" data-testid="ia-result-callback">
        <div className="flex items-start gap-4">
          <div className="h-11 w-11 rounded-xl bg-[#0055A4] text-white grid place-items-center flex-shrink-0">
            <PhoneCall className="h-5 w-5" />
          </div>
          <div>
            <div className="text-slate-900 font-semibold mb-1">Un spécialiste ECONATION va vous rappeler</div>
            <p className="text-slate-700 text-sm leading-relaxed">
              Un spécialiste vous rappellera pour vous <strong>confirmer le montant de toutes les aides</strong>,
              vérifier l'éligibilité définitive et vous accompagner dans le montage du dossier auprès de l'Anah et des obligés CEE.
            </p>
          </div>
        </div>
      </div>

      {/* Documents to prepare */}
      {result.documents_a_fournir?.length > 0 && (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5">
          <div className="text-slate-900 font-semibold mb-3 text-sm">📋 Documents à préparer pour le dossier</div>
          <ul className="space-y-1.5">
            {result.documents_a_fournir.map((d, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <Check className="h-4 w-4 text-[#10B981] mt-0.5 flex-shrink-0" strokeWidth={3} />
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-6 text-xs text-slate-500 leading-relaxed text-center">
        {result.disclaimer}
      </div>

      <div className="mt-6 flex justify-center">
        <button
          onClick={onClose}
          data-testid="ia-result-close"
          className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 px-6 py-3 text-white text-sm font-semibold tracking-wide transition"
        >
          Fermer <Check className="h-4 w-4" strokeWidth={3} />
        </button>
      </div>
    </div>
  );
}

/* ============================================================
 * SHARED UI
 * ============================================================ */
function StepTitle({ title, hint }) {
  return (
    <div className="mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-2">{title}</h2>
      {hint && <p className="text-slate-500 text-sm">{hint}</p>}
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function Radios({ label, options, value, onChange, testidPrefix }) {
  return (
    <div>
      <span className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={o.v}
            type="button"
            data-testid={`${testidPrefix}-${o.v}`}
            onClick={() => onChange(o.v)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition border ${
              value === o.v
                ? "bg-[#0055A4] text-white border-[#0055A4] shadow"
                : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
            }`}
          >
            {o.l}
          </button>
        ))}
      </div>
    </div>
  );
}

function InfoBanner({ text }) {
  return (
    <div className="mt-4 flex items-start gap-2 rounded-xl bg-[#EFF6FF] border border-[#0055A4]/15 p-3">
      <Info className="h-4 w-4 text-[#0055A4] mt-0.5 flex-shrink-0" />
      <span className="text-slate-700 text-xs leading-relaxed">{text}</span>
    </div>
  );
}

function Row({ label, value, highlight, strong, muted }) {
  return (
    <div className={`flex items-center justify-between px-5 py-3 border-b border-slate-100 last:border-b-0 ${strong ? "bg-slate-50" : ""}`}>
      <span className={`text-sm ${muted ? "text-slate-500" : "text-slate-700"} ${strong ? "font-semibold text-slate-900" : ""}`}>{label}</span>
      <span className={`text-sm font-semibold ${highlight ? "text-[#10B981]" : "text-slate-900"} ${strong ? "text-base text-[#0055A4]" : ""}`}>{value}</span>
    </div>
  );
}
